import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { acquireBandLeaderChannel, releaseBandLeaderChannel } from "../realtime/bandLeaderChannel";
import { safeRandomUUID } from "../capabilities";

const createPresenceKey = (leaderUserId) => {
  if (leaderUserId) return String(leaderUserId);
  return `viewer_${safeRandomUUID()}`;
};

const normalizeLeader = (payload, fallbackBandId) => {
  if (!payload?.leader_user_id) return null;
  return {
    leader_user_id: payload.leader_user_id,
    leader_username: payload.leader_username || payload.leader_name || "Leader",
    band_id: payload.band_id ?? fallbackBandId,
    app_opened_at: payload.app_opened_at || Date.now(),
  };
};

export default function useLeaderPresence({
  bandId,
  isLeader,
  leaderUserId,
  leaderName,
}) {
  const debugEnabled =
    typeof window !== "undefined" &&
    (window.location.search.includes("debugLeaderPresence=1") || localStorage.getItem("debugLeaderPresence") === "1");
  const [activeLeaders, setActiveLeaders] = useState([]);
  const [selectedLeaderId, setSelectedLeaderId] = useState(null);
  const [isOnline, setIsOnline] = useState(() => navigator?.onLine ?? true);
  const [isChannelReady, setIsChannelReady] = useState(false);
  const channelRef = useRef(null);
  const leadersRef = useRef(new Map());
  const presenceKeyRef = useRef(null);
  const announcedPresenceRef = useRef(false);
  const requestedPresenceRef = useRef(false);
  const channelBandIdRef = useRef(null);
  const storageKey = useMemo(
    () => (bandId ? `ct-follow-leader:${bandId}` : null),
    [bandId],
  );

  const syncLeaders = useCallback((nextMap) => {
    leadersRef.current = nextMap;
    const nextList = Array.from(nextMap.values()).sort((a, b) =>
      String(a.leader_username || "").localeCompare(String(b.leader_username || "")),
    );
    if (debugEnabled) {
      console.info("[leader-presence] sync leaders", {
        bandId,
        count: nextList.length,
        leaders: nextList.map((leader) => ({
          id: leader.leader_user_id,
          name: leader.leader_username,
        })),
      });
    }
    setActiveLeaders(nextList);
  }, [bandId, debugEnabled]);

  const upsertLeader = useCallback(
    (payload) => {
      const normalized = normalizeLeader(payload, bandId);
      if (!normalized) return;
      if (bandId && normalized.band_id && String(normalized.band_id) !== String(bandId)) return;
      const next = new Map(leadersRef.current);
      next.set(String(normalized.leader_user_id), normalized);
      syncLeaders(next);
    },
    [bandId, syncLeaders],
  );

  const removeLeader = useCallback(
    (leaderUserIdToRemove) => {
      if (!leaderUserIdToRemove) return;
      const next = new Map(leadersRef.current);
      next.delete(String(leaderUserIdToRemove));
      syncLeaders(next);
    },
    [syncLeaders],
  );

  const announcePresence = useCallback(({ force = false } = {}) => {
    if (!channelRef.current || !isOnline || !isLeader || !leaderUserId || !bandId) return;
    if (announcedPresenceRef.current && !force) return;
    announcedPresenceRef.current = true;
    const payload = {
      leader_user_id: leaderUserId,
      leader_username: leaderName || "Leader",
      band_id: bandId,
      app_opened_at: Date.now(),
    };
    if (debugEnabled) {
      console.info("[leader-presence] announce", {
        bandId,
        leaderUserId,
        leaderName,
        force,
      });
    }
    channelRef.current.send("leader_presence", payload);
    channelRef.current.track(payload);
    upsertLeader(payload);
  }, [bandId, isLeader, isOnline, leaderName, leaderUserId, upsertLeader]);

  const requestPresence = useCallback(({ force = false } = {}) => {
    if (!channelRef.current || !isOnline || !bandId) return;
    if (requestedPresenceRef.current && !force) return;
    requestedPresenceRef.current = true;
    if (debugEnabled) {
      console.info("[leader-presence] request leaders", { bandId });
    }
    channelRef.current.send("leader_presence_request", {
      band_id: bandId,
      requested_at: Date.now(),
    });
  }, [bandId, isOnline]);

  useEffect(() => {
    const onOnline = () => setIsOnline(true);
    const onOffline = () => setIsOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  useEffect(() => {
    if (!bandId || !isOnline) {
      if (debugEnabled) {
        console.info("[leader-presence] channel cleanup", { bandId, isOnline });
      }
      if (channelRef.current) {
        releaseBandLeaderChannel(channelBandIdRef.current);
        channelRef.current = null;
        channelBandIdRef.current = null;
      }
      setIsChannelReady(false);
      syncLeaders(new Map());
      return;
    }

    if (!presenceKeyRef.current) {
      presenceKeyRef.current = createPresenceKey(leaderUserId);
    }

    const channel = acquireBandLeaderChannel({
      bandId,
      presenceKey: presenceKeyRef.current,
    });
    channelBandIdRef.current = bandId;
    announcedPresenceRef.current = false;
    requestedPresenceRef.current = false;
    if (debugEnabled) {
      console.info("[leader-presence] subscribe", {
        bandId,
        isLeader,
        leaderUserId,
        presenceKey: presenceKeyRef.current,
      });
    }

    const unsubscribePresence = channel.addBroadcastListener("leader_presence", ({ payload }) => {
      if (debugEnabled) {
        console.info("[leader-presence] received presence", payload);
      }
      upsertLeader(payload);
    });

    const unsubscribeRequest = channel.addBroadcastListener("leader_presence_request", ({ payload }) => {
      if (debugEnabled) {
        console.info("[leader-presence] received request", payload);
      }
      if (!isLeader) return;
      if (payload?.band_id && bandId && String(payload.band_id) !== String(bandId)) return;
      announcePresence({ force: true });
    });

    const unsubscribeSync = channel.addPresenceListener("sync", () => {
      if (debugEnabled) {
        console.info("[leader-presence] presence sync");
      }
      const state = channel.presenceState();
      const next = new Map();
      Object.values(state || {}).forEach((entries) => {
        if (!Array.isArray(entries)) return;
        entries.forEach((entry) => {
          const normalized = normalizeLeader(entry, bandId);
          if (!normalized) return;
          next.set(String(normalized.leader_user_id), normalized);
        });
      });
      syncLeaders(next);
    });

    const unsubscribeJoin = channel.addPresenceListener("join", ({ newPresences }) => {
      if (debugEnabled) {
        console.info("[leader-presence] presence join", newPresences);
      }
      newPresences.forEach((entry) => {
        upsertLeader(entry);
      });
    });

    const unsubscribeLeave = channel.addPresenceListener("leave", ({ leftPresences }) => {
      if (debugEnabled) {
        console.info("[leader-presence] presence leave", leftPresences);
      }
      leftPresences.forEach((entry) => {
        if (entry?.leader_user_id) removeLeader(entry.leader_user_id);
      });
    });

    const unsubscribeStatus = channel.addStatusListener((status) => {
      if (debugEnabled) {
        console.info("[leader-presence] subscribe status", { bandId, status });
      }
      if (status === "SUBSCRIBED") {
        setIsChannelReady(true);
        if (isLeader) {
          announcePresence();
        } else {
          requestPresence();
        }
      }
      if (status !== "SUBSCRIBED") {
        setIsChannelReady(false);
      }
    });

    channelRef.current = channel;

    return () => {
      unsubscribePresence();
      unsubscribeRequest();
      unsubscribeSync();
      unsubscribeJoin();
      unsubscribeLeave();
      unsubscribeStatus();
      releaseBandLeaderChannel(channelBandIdRef.current);
      channelRef.current = null;
      channelBandIdRef.current = null;
      setIsChannelReady(false);
    };
  }, [announcePresence, bandId, isLeader, isOnline, leaderUserId, removeLeader, requestPresence, syncLeaders, upsertLeader]);

  useEffect(() => {
    if (!storageKey) {
      setSelectedLeaderId(null);
      return;
    }
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        setSelectedLeaderId(stored);
      } else {
        setSelectedLeaderId(null);
      }
    } catch (_) {
      setSelectedLeaderId(null);
    }
  }, [storageKey]);

  useEffect(() => {
    if (!storageKey) return;
    try {
      if (selectedLeaderId) {
        localStorage.setItem(storageKey, selectedLeaderId);
      } else {
        localStorage.removeItem(storageKey);
      }
    } catch (_) {
      // ignore storage failures
    }
  }, [selectedLeaderId, storageKey]);

  useEffect(() => {
    if (!isChannelReady) return;
    announcePresence();
  }, [announcePresence, isChannelReady]);

  const visibleLeaders = useMemo(() => {
    if (!leaderUserId) return activeLeaders;
    return activeLeaders.filter((leader) => String(leader.leader_user_id) !== String(leaderUserId));
  }, [activeLeaders, leaderUserId]);

  return {
    activeLeaders: visibleLeaders,
    selectedLeaderId,
    setSelectedLeaderId,
    isOnline,
    requestLeaderPresence: requestPresence,
  };
}
